import { useState, useCallback } from 'react';
import { RowConfig } from '@/shared/types/table';

export const useTableExpand = <T>(initialExpandedIds: (string | number)[] = []) => {
  const [expandedIds, setExpandedIds] = useState<Set<string | number>>(new Set(initialExpandedIds));

  const handleRowExpand = useCallback((row: RowConfig<T>) => {
    setExpandedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(row.id)) {
        newSet.delete(row.id);
      } else {
        newSet.add(row.id);
      }
      return newSet;
    });
  }, []);

  const expandRow = useCallback((rowId: string | number) => {
    setExpandedIds((prev) => new Set(prev).add(rowId));
  }, []);

  const collapseRow = useCallback((rowId: string | number) => {
    setExpandedIds((prev) => {
      const newSet = new Set(prev);
      newSet.delete(rowId);
      return newSet;
    });
  }, []);

  const toggleRow = useCallback((rowId: string | number) => {
    setExpandedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(rowId)) {
        newSet.delete(rowId);
      } else {
        newSet.add(rowId);
      }
      return newSet;
    });
  }, []);

  const expandAll = useCallback((rows: RowConfig<T>[]) => {
    const allParentIds = rows.filter((row) => row.isParent).map((row) => row.id);
    setExpandedIds(new Set(allParentIds));
  }, []);

  const collapseAll = useCallback(() => {
    setExpandedIds(new Set());
  }, []);

  const isExpanded = useCallback(
    (rowId: string | number) => {
      return expandedIds.has(rowId);
    },
    [expandedIds]
  );

  const applyExpandedState = useCallback(
    (rows: RowConfig<T>[]): RowConfig<T>[] => {
      return rows.map((row) => ({
        ...row,
        expanded: expandedIds.has(row.id),
      }));
    },
    [expandedIds]
  );

  return {
    expandedIds: Array.from(expandedIds),
    expandedCount: expandedIds.size,
    handleRowExpand,
    expandRow,
    collapseRow,
    toggleRow,
    expandAll,
    collapseAll,
    isExpanded,
    applyExpandedState,
  };
};
